import React from 'react';
import { formatCurrency, formatHours } from '../../utils/helpers';

const Summary = ({ formData, allocations }) => {

    const {
        sprintsMilestones = [],
        resourceAllocationGrid = [],
        projectExpenses = [],
        resourceTable = [],
        businessHoursPerDay = 8
    } = formData;

    const allAllocations = allocations.flatMap(a => a.allocations || []);
    const totalResources = formData.preferredResources.length;
    // const totalAllocatedHours = allAllocations.reduce((acc, cur) => acc + (parseFloat(cur.hours) || 0), 0);
    const totalDurationWeeks = formData.sprintsMilestones.reduce((acc, milestone) => acc + (milestone.duration || 0), 0);
    // const totalBandwidth = totalResources * formData.businessHoursPerDay * totalDurationWeeks * 5; // Assuming 5 working days per week
    // const totalUtilization = totalBandwidth > 0 ? ((totalAllocatedHours / totalBandwidth) * 100).toFixed(2) : '0.00';
    const totalCost = allAllocations.reduce((acc, cur) => acc + (parseFloat(cur.cost) || 0), 0).toFixed(2);
    // const totalMaterialCost = formData.projectExpenses.reduce((sum, expense) => sum + (expense.quantity * expense.unitPrice), 0);
    // const totalProjectCost = parseFloat(totalCost) + totalMaterialCost;


    const totalResourceCost = resourceAllocationGrid.reduce((sum, sprintRow) => {
        return sum + Object.entries(sprintRow.allocations || {}).reduce((sprintSum, [resourceId, allocation]) => {
            const resource = resourceTable.find((res) => res.id === resourceId);
            const hourlyRate = resource?.reference?.hourlyRate || 0;
            const allocatedHours = allocation?.allocatedHours || 0;
            const cost = allocatedHours * hourlyRate;

            //console.log(`💰 Resource Cost: ${resourceId} - Hours: ${allocatedHours}, Rate: ${hourlyRate}, Cost: ${cost}`);
            return sprintSum + cost;
        }, 0);
    }, 0);

    const totalMaterialCost = projectExpenses.reduce((sum, expense) => {
        const cost = (expense.quantity || 0) * (expense.unitPrice || 0);
        //console.log(`🛠️ Material Cost: ${expense.item} - Quantity: ${expense.quantity}, Unit Price: ${expense.unitPrice}, Cost: ${cost}`);
        return sum + cost;
    }, 0);

    const totalProjectCost = totalResourceCost + totalMaterialCost;

    // 2. **Total Duration (Weeks)**
    const totalDuration = sprintsMilestones.reduce((sum, sprint) => sum + (sprint.duration || 0), 0);

    // 3. **Total Number of Sprints**
    const totalSprints = sprintsMilestones.length;

    // 4. **Number of Resources Used**
    const numberOfResources = resourceTable.length;

    // 5. **Total Resource Bandwidth Calculation**
    const totalResourceBandwidth = resourceAllocationGrid.reduce((sum, sprintRow) => {
        const allocatedResources = Object.values(sprintRow.allocations || {}).filter(
            (allocation) => (allocation.allocatedHours || 0) > 0
        ).length;

        const workingDays = sprintRow.sprint?.workingDays || 0;
        const bandwidth = workingDays * (businessHoursPerDay || 8) * allocatedResources;

        //console.log(`📊 Sprint: ${sprintRow.sprint?.name} - Working Days: ${workingDays}, Resources: ${allocatedResources}, Bandwidth: ${bandwidth}`);

        return sum + bandwidth;
    }, 0);

    // 6. **Total Resource Allocation (Total Allocated Hours)**
    const totalResourceAllocation = resourceAllocationGrid.reduce((sum, sprintRow) => {
        return sum + Object.values(sprintRow.allocations || {}).reduce((sprintSum, allocation) => {
            return sprintSum + (allocation.allocatedHours || 0);
        }, 0);
    }, 0);

    // 7. **Utilization Percentage**
    const utilizationPercentage = totalResourceBandwidth > 0
        ? ((totalResourceAllocation / totalResourceBandwidth) * 100).toFixed(2)
        : "0.00";

    // Map numeric values to actual weekday names
    const weekendMapping = {
        0: "Sunday",
        1: "Monday",
        2: "Tuesday",
        3: "Wednesday",
        4: "Thursday",
        5: "Friday",
        6: "Saturday"
    };

    // Convert user-selected weekend days into readable format
    const selectedWeekends = formData.weekendDays.map(day => weekendMapping[day]).join(" & ");


    const projectSummary = {
        projectName: formData.projectName,
        kickoffDate: formData.startDate,
        implementationType: formData.implementationType,
        totalProjectCost: totalProjectCost,
        totalMaterialCost: totalMaterialCost,
        totalResourceCost: totalResourceCost,
        totalDurationWeeks: totalDuration,
        totalMilestones: totalSprints,
        totalResources: numberOfResources,
        totalResourceBandwidth: totalResourceBandwidth,
        totalResourceAllocation: totalResourceAllocation,
        utilizationRate: utilizationPercentage,
        holidays: formData.holidays.join(', '),
        weekends: selectedWeekends || "None",
        businessHoursPerDay: formData.businessHoursPerDay
    };

    return (
        <div className="p-4 bg-gray-100 rounded-md shadow-md mb-4">
            <h2 className="text-xl font-bold mb-2">Estimated Project Summary</h2>
            <p>
                The <strong>{projectSummary.projectName}</strong> project is set to kick off on <strong>{projectSummary.kickoffDate}</strong> and will follow a <strong>{projectSummary.implementationType}</strong> methodology. Over a span of <strong> {projectSummary.totalDurationWeeks} weeks</strong>, the project consists of <strong> {projectSummary.totalMilestones} {projectSummary.implementationType === "Agile" ? "sprints" : "milestones"}</strong> serving as key deliverables.  <br></br><br></br>

                A dedicated team of <strong>{projectSummary.totalResources}</strong> resources will manage an available bandwidth of <strong>{formatHours(projectSummary.totalResourceBandwidth)}</strong>, with a total workload allocation of <strong> {formatHours(projectSummary.totalResourceAllocation)}</strong>, achieving an overall utilization rate of <strong> {projectSummary.utilizationRate}%</strong>.  <br></br><br></br>

                The total estimated project cost is <strong>{formatCurrency(projectSummary.totalProjectCost)}</strong>, which includes <strong>{formatCurrency(projectSummary.totalMaterialCost)}</strong> in project expenses and <strong> {formatCurrency(projectSummary.totalResourceCost)}</strong> in resource-related costs.  <br></br><br></br>

                The project will operate with a standard working schedule of <strong>{projectSummary.businessHoursPerDay} hours per day</strong>, observing weekends on <strong>{projectSummary.weekends}</strong>. The following holidays will be accounted for: <strong> {projectSummary.holidays}</strong>.  <br></br><br></br>

                Additional taxes and fees may apply based on the project's location and regulatory requirements.

                This structured approach ensures efficient resource utilization, cost-effectiveness, and strategic execution, laying a strong foundation for successful project delivery.

            </p>
            
        </div>
    );
};

export default Summary;
